import React, { useEffect, useRef } from "react";
import CheckoutButton from "./CheckoutButton";

const SnapshotScore: React.FC = () => {
  const scoreRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Animate score counter on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-score");
          }
        });
      },
      { threshold: 0.2 },
    );

    const currentScoreRef = scoreRef.current;
    const currentCardRef = cardRef.current;

    if (currentScoreRef) {
      observer.observe(currentScoreRef);
    }

    if (currentCardRef) {
      observer.observe(currentCardRef);
    }

    return () => {
      if (currentScoreRef) {
        observer.unobserve(currentScoreRef);
      }
      if (currentCardRef) {
        observer.unobserve(currentCardRef);
      }
    };
  }, []);

  return (
    <section className="snapshot-score-section" id="snapshot-score">
      {/* Background elements */}
      <div className="snapshot-stars-container">
        {[...Array(30)].map((_, index) => (
          <div
            key={index}
            className="snapshot-star"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${0.5 + Math.random() * 2}px`,
              height: `${0.5 + Math.random() * 2}px`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="camera-orbit">
        <div className="camera-orbit-path"></div>
        <div className="camera-icon">📸</div>
      </div>

      <div className="container">
        <div className="section-header">
          <h2 className="snapshot-title">
            Introducing{" "}
            <span className="highlight shimmer-text">Snapshot Score</span>
          </h2>
          <p className="snapshot-subtitle">
            Elevate your satellite photography with precision metrics
          </p>
        </div>

        <div className="snapshot-content">
          <div className="snapshot-text-container" ref={scoreRef}>
            <div className="snapshot-text">
              <p className="snapshot-lead">
                Our proprietary system helps you capture stunning satellite
                photos with scientific precision:
              </p>

              <ul className="feature-list">
                <li className="feature-item">
                  <div className="feature-icon">
                    <span className="cosmic-icon">✨</span>
                  </div>
                  <div className="feature-details">
                    <h3>Advanced Algorithm</h3>
                    <p>
                      Combines brightness, elevation, and real-time weather
                      clarity
                    </p>
                  </div>
                </li>

                <li className="feature-item">
                  <div className="feature-icon">
                    <span className="cosmic-icon">📊</span>
                  </div>
                  <div className="feature-details">
                    <h3>Precision Rating</h3>
                    <p>
                      Rates each satellite pass on a precise 1-10 scale for
                      quality
                    </p>
                  </div>
                </li>

                <li className="feature-item">
                  <div className="feature-icon">
                    <span className="cosmic-icon">🎯</span>
                  </div>
                  <div className="feature-details">
                    <h3>Optimal Settings</h3>
                    <p>
                      Provides specific camera settings calibrated for each pass
                    </p>
                  </div>
                </li>

                <li className="feature-item">
                  <div className="feature-icon">
                    <span className="cosmic-icon">🔔</span>
                  </div>
                  <div className="feature-details">
                    <h3>Prime Shot Alerts</h3>
                    <p>
                      Identifies prime photography opportunities with push
                      notifications
                    </p>
                  </div>
                </li>
              </ul>

              <div className="snapshot-cta">
                <a href="/features" className="learn-more-link">
                  <span>View sample shots</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="snapshot-card-container" ref={cardRef}>
            <div className="snapshot-card">
              <div className="card-header">
                <h3>Snapshot Score</h3>
                <div className="quality-label">SpacePings Premium</div>
              </div>

              <div className="card-content">
                <div className="score-display">
                  <div className="score-gauge">
                    <svg viewBox="0 0 120 120" className="gauge-svg">
                      <circle
                        className="gauge-background"
                        cx="60"
                        cy="60"
                        r="54"
                      />
                      <circle
                        className="gauge-fill"
                        cx="60"
                        cy="60"
                        r="54"
                        pathLength="100"
                        strokeDasharray="100"
                        strokeDashoffset="20"
                      />
                      <text
                        x="60"
                        y="60"
                        className="gauge-text"
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        8
                      </text>
                    </svg>
                    <div className="gauge-scale">
                      <span>0</span>
                      <span>10</span>
                    </div>
                  </div>

                  <div className="score-details">
                    <div className="rating">PRIME SHOT</div>
                    <div className="score-timestamp">March 12, 08:15 PM</div>
                    <div className="score-satellite">STARLINK-4432</div>
                  </div>
                </div>

                <div className="camera-settings-container">
                  <h4>Recommended Settings</h4>
                  <div className="camera-settings">
                    <div className="setting">
                      <div className="setting-name">ISO</div>
                      <div className="setting-value">1600</div>
                    </div>
                    <div className="setting">
                      <div className="setting-name">Aperture</div>
                      <div className="setting-value">f/2.8</div>
                    </div>
                    <div className="setting">
                      <div className="setting-name">Shutter</div>
                      <div className="setting-value">2-sec</div>
                    </div>
                  </div>
                </div>

                <div className="snapshot-factors">
                  <div className="factor">
                    <div className="factor-name">Brightness</div>
                    <div className="factor-bar">
                      <div
                        className="factor-fill"
                        style={{ width: "85%" }}
                      ></div>
                    </div>
                  </div>
                  <div className="factor">
                    <div className="factor-name">Elevation</div>
                    <div className="factor-bar">
                      <div
                        className="factor-fill"
                        style={{ width: "70%" }}
                      ></div>
                    </div>
                  </div>
                  <div className="factor">
                    <div className="factor-name">Weather</div>
                    <div className="factor-bar">
                      <div
                        className="factor-fill"
                        style={{ width: "90%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <CheckoutButton
                  plan="premium_monthly"
                  customClass="snapshot-cta-button"
                />
              </div>

              <div className="card-decoration">
                <div className="orbit-circle"></div>
                <div className="orbit-satellite"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SnapshotScore;
